import SubscriptionService from '@/services/SubscriptionService';
import { Module } from 'vuex';
import {
  PostSubscription, Subscription, SubscriptionListSort, SubscriptionFeedEntry, subscriptionFeedFilter
} from '@/views/subscriptions/models/Subscription';
import {
  filter, keyBy, orderBy
} from 'lodash';
import TaggingService, { TagSettings } from '@/services/TaggingService';
import TranslatedBackendErrors from '@/utils/TranslatedBackendErrors';

interface State {
  subscriptionList: Subscription[];
  subscriptionListSort: SubscriptionListSort;
  subscriptionFeed: SubscriptionFeedEntry[];
  subscriptionFeedFilter: subscriptionFeedFilter;
  loadingFeed: boolean;
  loadingSubscriptions: boolean;
  allTagSettings: TagSettings;
}

const module: Module<State, any> = {
  namespaced: true,
  state: {
    subscriptionList: [],
    subscriptionListSort: {
      sortBy: 'TIMESTAMP',
      sortOrder: 'DESC'
    },
    subscriptionFeed: [],
    subscriptionFeedFilter: 'ALL',
    loadingFeed: true,
    loadingSubscriptions: true,
    allTagSettings: { heuristic: true }
  },
  getters: {

    getFilteredSubscriptionFeed: (state: State) => state.subscriptionFeed.filter((subscription) => {
      switch (state.subscriptionFeedFilter) {
        case 'ALL':
          return true;
        case 'TALKS':
          return subscription.type.includes('FORUM_TALK_');
        case 'PROJECTS':
          return subscription.type.includes('PROJECT');
        case 'TAGS':
          return subscription.type.includes('TAG');
        default:
          return false;
      }
    }),

    getSubscriptionsByObjectId: ({ subscriptionList }) => keyBy(subscriptionList, 'subscribedObjectIdentifier'),

    getSubsciptionListSorted: (state: State) => {
      const { sortBy } = state.subscriptionListSort;
      const sortOrder: 'asc' | 'desc' = state.subscriptionListSort.sortOrder.toLowerCase() as 'asc' | 'desc';

      switch (sortBy) {
        case 'TITLE':
          return orderBy(state.subscriptionList,
            [(subscription) => (subscription.title === 'PROJECTS' ? 'all projects' : subscription.title.toLowerCase())],
            sortOrder);
        case 'HOW':
          return orderBy(state.subscriptionList,
            (subscription) => {
              let { title } = subscription;
              const { createdType } = subscription;
              let sortCreatedType: string = createdType;
              if (title === 'PROJECTS') {
                title = 'all projects';
              }
              if (subscription.type === 'TAG' && createdType === 'SYSTEM') {
                sortCreatedType = 'SKILLS';
              } else if (createdType === 'SYSTEM') {
                sortCreatedType = 'AUTO';
              }
              return [sortCreatedType, title.toLowerCase()];
            },
            [sortOrder, 'asc']);
        case 'WHAT':
          return orderBy(state.subscriptionList, (subscription) => {
            let { title } = subscription;
            const { type } = subscription;
            let sortType: string;

            if (title === 'PROJECTS') {
              title = 'all projects';
            }
            if (type === 'FORUM_TALK') {
              sortType = 'Talk';
            } else if (type === 'FORUM_TALKS') {
              sortType = 'Talks';
            } else if (type === 'TAG') {
              sortType = 'Tag';
            } else {
              sortType = 'Project';
            }
            return [sortType, title.toLowerCase()];
          },
          [sortOrder, 'asc']);
        case 'TIMESTAMP':
        default:
          return state.subscriptionList;
      }
    }

  },
  mutations: {
    setSubscriptionsList(state, subscriptionsList: Subscription[]) {
      state.subscriptionList = subscriptionsList;
    },
    setSubscriptionFeed(state, subscriptionFeed: SubscriptionFeedEntry[]) {
      state.subscriptionFeed = subscriptionFeed;
    },
    setSubscriptionFeedFilter(state, filter: subscriptionFeedFilter) {
      state.subscriptionFeedFilter = filter;
    },
    setSubscriptionListSort(state, sort: SubscriptionListSort) {
      state.subscriptionListSort = sort;
    },
    setLoadingFeed(state, loadingStatus) {
      state.loadingFeed = loadingStatus;
    },
    setLoadingSubscriptions(state, loadingStatus) {
      state.loadingSubscriptions = loadingStatus;
    },
    removeSubscription(state, subscriptionId) {
      state.subscriptionList = filter(state.subscriptionList, (subscription) => subscription.subscriptionId !== subscriptionId);
    },
    setAllTagSettings(state, settings) {
      state.allTagSettings = settings ?? state.allTagSettings;
    }
  },
  actions: {

    fetchSubscriptions({ commit }) {
      commit('setLoadingSubscriptions', true);
      SubscriptionService.getAllSubscriptions().then((subscriptionList) => {
        commit('setSubscriptionsList', subscriptionList.subscriptionList);
      }).finally(() => {
        commit('setLoadingSubscriptions', false);
      });
    },

    fetchSubscriptionFeed({ commit }) {
      commit('setLoadingFeed', true);
      SubscriptionService.getSubscriptionFeed().then((subscriptionFeed) => {
        commit('setSubscriptionFeed', subscriptionFeed.feedEntryList);
      }).finally(() => {
        commit('setLoadingFeed', false);
      });
    },

    postSubscription({ commit }, postSubscription: PostSubscription) {
      return SubscriptionService.postSubscriptions(postSubscription)
        .then((subscription) => {
          commit('setSubscriptionsList', [...this.state.subscriptions.subscriptionList, subscription]);
        })
        .catch((error: any) => {
          const translatedError = new TranslatedBackendErrors(error.response);
          translatedError.outputGlobalErrorMessage();
          throw error; // Re-throw so the caller (toggleFavorite) can catch it
        });
    },

    deleteSubscription({ commit }, subscriptionId: string) {
      return SubscriptionService.deleteSubscription(subscriptionId)
        .then(() => {
          commit('removeSubscription', subscriptionId);
        })
        .catch((error: any) => {
          const translatedError = new TranslatedBackendErrors(error.response);
          translatedError.outputGlobalErrorMessage();
          throw error; // Re-throw so the caller (toggleFavorite) can catch it
        });
    },

    clearSubscriptionFeed({ commit }) {
      commit('setSubscriptionFeed', []);
    },

    clearSubscriptionsList({ commit }) {
      commit('setSubscriptionsList', []);
    },

    filterSubscriptionFeed({ commit }, filter: subscriptionFeedFilter) {
      commit('setSubscriptionFeedFilter', filter);
    },

    setSubscriptionListSort({ commit }, sort: SubscriptionListSort) {
      commit('setSubscriptionListSort', sort);
    },
    setLoadingFeed({ commit }, stateLoading: boolean) {
      commit('setLoadingFeed', stateLoading);
    },
    setLoadingSubscriptions({ commit }, stateLoading: boolean) {
      commit('setLoadingSubscriptions', stateLoading);
    },

    async fetchAllTagSettings({ commit }) {
      const settings = await TaggingService.getAllTagSettings();
      commit('setAllTagSettings', settings);
      return settings;
    },

    async updateAllTagSettings({ commit }, payload: TagSettings) {
      const settings = await TaggingService.updateAllTagSettings(payload);
      commit('setAllTagSettings', settings);
      return settings;
    }
  },
};

export default module;
